import type { ScreenerCVDCandle } from '@/types'
import type { ScalePoint } from './chartScale'
import { findLastKnownIndex } from './chartUtils'

type PlottedCVDCandle = ScreenerCVDCandle & {
  open: number; high: number; low: number; close: number; carriedForward?: boolean
}

export function buildCVDPlot(candles: readonly ScreenerCVDCandle[]) {
  const points: PlottedCVDCandle[] = []
  const ordered = [...candles].filter((candle) => Number.isFinite(candle.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp)
  for (const candle of ordered) {
    const valid =
    [candle.timestamp, candle.open, candle.high, candle.low, candle.close]
      .every((value) => typeof value === 'number' && Number.isFinite(value))
    && candle.open != null && candle.close != null && candle.low != null && candle.high != null
    && candle.low <= Math.min(candle.open, candle.close)
    && candle.high >= Math.max(candle.open, candle.close)
    if (valid) {
      points.push({ ...candle } as PlottedCVDCandle)
    } else if (points.length > 0) {
      const close = points[points.length - 1].close
      // Carry the last recorded total for display only. An absent trade bucket
      // cannot distinguish a quiet market from missing collection coverage.
      points.push({ ...candle, open: close, high: close, low: close, close,
        delta: null, complete: false, resolution: null, carriedForward: true })
    }
  }
  const timestamps = points.map((candle) => candle.timestamp * 1000)
  const series = points.map((point) => [point.timestamp * 1000, point.close])
  // Match the step line when fitting a viewport between recorded buckets.
  const scaleSeries: ScalePoint[][] = [points.flatMap((point, index) => [
    ...(index > 0 ? [[point.timestamp * 1000, points[index - 1].close] as ScalePoint] : []),
    [point.timestamp * 1000, point.close] as ScalePoint,
  ])]
  return { points, timestamps, series, scaleSeries, latest: points[points.length - 1] ?? null }
}

export function cvdCandleAtTime(
  plot: Pick<ReturnType<typeof buildCVDPlot>, 'points' | 'timestamps'>,
  timestampMs: number,
  intervalSeconds: number,
): PlottedCVDCandle | null {
  if (!Number.isFinite(timestampMs) || !(intervalSeconds > 0)) return null
  const index = findLastKnownIndex(plot.timestamps, timestampMs)
  const point = index < 0 ? null : plot.points[index]
  return point && timestampMs < (point.timestamp + intervalSeconds) * 1000 ? point : null
}
